"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useClientSidebar } from "@/components/sidebar/client-sidebar-context";
import { icons } from "@/components/sidebar/nav-icons";
import { newSitemapPath, newSocialPostPath } from "@/lib/publishing/paths";

interface CreateItem {
  key: string;
  label: string;
  href: string;
  iconKey: string;
  /** Rendered under a divider, separating company work from agency work. */
  dividerBefore?: boolean;
}

/**
 * Quick-create menu, the "+" beside search.
 *
 * Staff only. Client users can be granted create rights per company, but those
 * are high-risk grants an admin opts into one at a time; offering the action
 * from global chrome and having the route refuse it would be worse than not
 * offering it, so this stays with the roles that always hold the rights.
 *
 * The company entries appear only inside a company, and only for modules that
 * company actually has switched on.
 */
export function CreateMenu({ variant = "topbar" }: { variant?: "topbar" | "drawer" }) {
  const { client } = useClientSidebar();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const drawer = variant === "drawer";

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items: CreateItem[] = [];
  if (client) {
    const id = client.clientId;
    items.push(
      { key: "report", label: "Report", href: `/clients/${id}/reports/new`, iconKey: "folder" },
      { key: "campaign", label: "Campaign", href: `/clients/${id}/campaigns/new`, iconKey: "trendingUp" },
    );
    if (client.socialEnabled) {
      items.push({ key: "post", label: "Social post", href: newSocialPostPath(id), iconKey: "send" });
    }
    if (client.sitemapsEnabled) {
      items.push({ key: "sitemap", label: "Sitemap", href: newSitemapPath(id), iconKey: "shareNodes" });
    }
  }
  items.push(
    { key: "company", label: "Company", href: "/clients/new", iconKey: "grid", dividerBefore: items.length > 0 },
    { key: "user", label: "User", href: "/admin/users/new", iconKey: "users" },
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Create"
        title="Create"
        className={drawer
          ? "flex min-h-11 w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong"
          : "flex h-8 w-8 items-center justify-center rounded-md text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong"
        }
      >
        <svg
          width={17}
          height={17}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {drawer && <span>Create</span>}
      </button>

      {open && (
        <div
          role="menu"
          className={`spark-menu-surface absolute z-[70] w-56 rounded-lg border border-spark-hairline bg-spark-surface p-1.5 shadow-[var(--shadow-card-hover)] ${
            drawer ? "bottom-full left-0 mb-2" : "right-0 top-full mt-2"
          }`}
        >
          <p className="px-2 pb-1 pt-1 text-[11px] font-bold uppercase tracking-[.1em] text-spark-gray">
            Create new
          </p>
          {items.map((item) => (
            <div key={item.key}>
              {item.dividerBefore && <div className="my-1 border-t border-spark-hairline" />}
              <Link
                href={item.href}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-spark-navigation-text transition-colors hover:bg-spark-navigation-hover hover:text-spark-navigation-strong"
              >
                <span className="shrink-0">{icons[item.iconKey]}</span>
                {item.label}
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
